import React, { useState, useEffect } from 'react';
import { Download, CalendarDays, Loader2, FileText, Plane, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { downloadBlob } from '../../utils/downloadBlob';

const API = process.env.REACT_APP_BACKEND_URL;

const LEAVE_TYPE_CONFIG = {
  annual:          { label: 'Annual', color: 'bg-blue-100 text-blue-700' },
  sick:            { label: 'Sick', color: 'bg-red-100 text-red-700' },
  emergency:       { label: 'Emergency', color: 'bg-orange-100 text-orange-700' },
  unpaid:          { label: 'Unpaid', color: 'bg-gray-100 text-gray-600' },
  partial_unpaid:  { label: 'Partial Unpaid', color: 'bg-violet-100 text-violet-700' },
  public_holiday:  { label: 'Public Holiday', color: 'bg-teal-100 text-teal-700' },
};

const STATUS_CONFIG = {
  approved:  { label: 'Approved', color: 'bg-green-50 text-green-600' },
  completed: { label: 'Completed', color: 'bg-sky-50 text-sky-600' },
  rejected:  { label: 'Rejected', color: 'bg-red-50 text-red-600' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const calcDays = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  return Math.round((e - s) / 86400000) + 1;
};

const PortalLeaveSalary = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/my-leaves`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Show paid/partially-paid leaves only — fully unpaid types (unpaid,
        // mgmt_unpaid) have no leave salary, so they are not listed here.
        const HIDDEN_TYPES = ['unpaid', 'mgmt_unpaid'];
        const approvedLeaves = (res.data || []).filter(l =>
          (l.status === 'approved' || l.status === 'completed') &&
          !HIDDEN_TYPES.includes(l.leave_type)
        );
        setLeaves(approvedLeaves);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const handleDownload = async (leave) => {
    setDownloadingId(leave.id);
    try {
      const startDt = new Date(leave.start_date + 'T00:00:00');
      const params = new URLSearchParams({
        year: startDt.getFullYear(),
        month: startDt.getMonth() + 1,
        leave_type: leave.leave_type,
        leave_start_date: leave.start_date,
        leave_end_date: leave.end_date,
      });

      const res = await axios.get(`${API}/api/employee-portal/my-leave-salary?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      downloadBlob(res.data, `Leave_Salary_${leave.start_date}_to_${leave.end_date}.pdf`, 'application/pdf');
    } catch {
      alert('Failed to download. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-leave-salary">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
          <CalendarDays className="h-5 w-5 text-teal-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Leave Salary</h2>
          <p className="text-xs text-gray-400">Your leave records with salary calculations</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
        </div>
      ) : leaves.length === 0 ? (
        <Card className="border-dashed border-0 shadow-sm">
          <CardContent className="p-10 text-center">
            <Plane className="h-10 w-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No leave records found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {leaves.map(leave => {
            const ltc = LEAVE_TYPE_CONFIG[leave.leave_type] || { label: leave.leave_type, color: 'bg-gray-100 text-gray-600' };
            const sc = STATUS_CONFIG[leave.status] || STATUS_CONFIG.completed;
            const days = leave.days || calcDays(leave.start_date, leave.end_date);
            const isDownloading = downloadingId === leave.id;

            return (
              <Card key={leave.id} className="border-0 shadow-sm" data-testid={`leave-record-${leave.id}`}>
                <CardContent className="p-3.5">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <Badge className={`text-[9px] px-1.5 py-0 ${ltc.color}`}>{ltc.label}</Badge>
                        <Badge className={`text-[9px] px-1.5 py-0 ${sc.color}`}>{sc.label}</Badge>
                      </div>
                      <p className="text-sm font-semibold text-gray-800">
                        {formatDate(leave.start_date)} — {formatDate(leave.end_date)}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                        <span>{days} {days === 1 ? 'day' : 'days'}</span>
                        {leave.reason && <span className="truncate max-w-[180px]">{leave.reason}</span>}
                      </div>
                    </div>
                    {leave.leave_type !== 'public_holiday' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 px-3 border-teal-200 text-teal-700 hover:bg-teal-50 flex-shrink-0"
                        onClick={() => handleDownload(leave)}
                        disabled={isDownloading}
                        data-testid={`download-leave-${leave.id}`}
                      >
                        {isDownloading
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Download className="h-3.5 w-3.5" />
                        }
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="text-center pt-1">
        <Badge variant="outline" className="text-[10px] text-gray-400">
          <FileText className="h-3 w-3 mr-1" /> Based on UAE labour law calculations
        </Badge>
      </div>
    </div>
  );
};

export default PortalLeaveSalary;
